import BpmnModdle from "bpmn-moddle";
import { diff } from "bpmn-js-differ";

const CHANGE_TYPES = [
  { key: "_removed", label: "Removed" },
  { key: "_added", label: "Added" },
  { key: "_changed", label: "Changed" },
  { key: "_layoutChanged", label: "Layout Changed" },
];
const NON_ACTIONABLE_CHANGED_TYPES = new Set([
  "Collaboration",
  "Definitions",
  "Lane",
  "LaneSet",
  "Participant",
  "Process",
]);

async function parseDefinitions(xml) {
  const moddle = new BpmnModdle();
  const { rootElement } = await moddle.fromXML(xml);
  return rootElement;
}

function cleanType(type = "") {
  return type.replace(/^bpmn:/, "") || "Unknown";
}

function getChangedElement(change) {
  return change?.model || change;
}

function readableElementName(element) {
  return element?.name || element?.id || "Unnamed element";
}

function readableReferenceName(reference) {
  return Array.isArray(reference)
    ? reference.map(readableElementName).join(", ")
    : readableElementName(reference);
}

function readableConnectionName(element) {
  if (!element?.sourceRef || !element?.targetRef) return "";
  const sourceName = readableReferenceName(element.sourceRef);
  const targetName = readableReferenceName(element.targetRef);
  return `${sourceName} → ${targetName}`;
}

function mapChangeEntry([id, change], label) {
  const element = getChangedElement(change);
  const connectionName = readableConnectionName(element);

  return {
    id,
    name: connectionName || element?.name || "Unnamed element",
    type: cleanType(element?.$type),
    change: label,
  };
}

function actionableEntries(changes, key) {
  const entries = Object.entries(changes[key] || {});
  if (key !== "_changed") return entries;
  return entries.filter(([, change]) => {
    const type = cleanType(getChangedElement(change)?.$type);
    return !NON_ACTIONABLE_CHANGED_TYPES.has(type);
  });
}

export async function diffBpmnModels(beforeXml, afterXml) {
  const beforeDefinitions = await parseDefinitions(beforeXml);
  const afterDefinitions = await parseDefinitions(afterXml);
  const changes = diff(beforeDefinitions, afterDefinitions);

  const rows = CHANGE_TYPES.flatMap(({ key, label }) =>
    actionableEntries(changes, key).map((entry) =>
      mapChangeEntry(entry, label)
    )
  );
  const changedIds = actionableEntries(changes, "_changed").map(([id]) => id);

  const beforeMarkers = [
    ...Object.keys(changes._removed || {}).map((id) => ({ id, className: "change_removed" })),
    ...changedIds.map((id) => ({ id, className: "change_changed" })),
    ...Object.keys(changes._layoutChanged || {}).map((id) => ({ id, className: "change_layout_changed" })),
  ];
  const afterMarkers = [
    ...Object.keys(changes._added || {}).map((id) => ({ id, className: "change_added" })),
    ...changedIds.map((id) => ({ id, className: "change_changed" })),
    ...Object.keys(changes._layoutChanged || {}).map((id) => ({ id, className: "change_layout_changed" })),
  ];

  return { rows, beforeMarkers, afterMarkers };
}
